import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const TOKEN_KEY = "wafa.admin.token";
const USER_KEY = "wafa.admin.user";

/** Read the stored token. Kept in localStorage so it survives reloads. */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/** Attach the bearer to an axios config. */
export function authHeaders() {
  const t = getToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const t = getToken();
    if (!t) { setUser(null); return null; }
    try {
      const { data } = await api.get("/auth/me", authHeaders());
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      return data;
    } catch (_) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const changePassword = useCallback(async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password }, authHeaders());
    await refresh();
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Convert FastAPI error responses to a display string. */
export function apiErrorMessage(err, fallback = "Something went wrong.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return fallback;
}
